/* eslint-disable */
import { getDistributionPeriod, getRMDRequiredAge } from "./tables";
import { inheritedRMDForYear, projectInherited } from "./inherited";

// Account classification. Inherited accounts are never aggregated with owned.
export const isOwned = (a) => !a.is_inherited_ira;
export const isInherited = (a) => !!a.is_inherited_ira;
export const ownedIRAs = (accounts) => (accounts || []).filter(a => isOwned(a) && !a.is_annuity);
export const ownedAnnuities = (accounts) => (accounts || []).filter(a => isOwned(a) && a.is_annuity);
export const inheritedAccounts = (accounts) => (accounts || []).filter(isInherited);

export function ageAtYearEnd(dob, year = new Date().getFullYear()) {
  return year - new Date(dob).getFullYear();
}

// Owned IRA balance available to the Scenario engine (non-annuity, non-inherited).
export function scenarioBalance(client) {
  return ownedIRAs(client?.accounts).reduce((s, a) => s + (a.balance || 0), 0);
}

export function calculateRMD(client) {
  const currentYear = new Date().getFullYear();
  const dob = new Date(client.dob);
  const ageYE = currentYear - dob.getFullYear();
  const rmdAge = getRMDRequiredAge(dob.getFullYear());
  const accounts = client.accounts || [];

  // ── inherited accounts: each on its own line ──
  const inherited = inheritedAccounts(accounts).map(a => {
    const r = inheritedRMDForYear(a, client.dob, currentYear);
    return { id: a.id, account_number: a.account_number, balance: a.balance || 0, is_annuity: !!a.is_annuity, ...r };
  });
  const inheritedTotal = inherited.reduce((s, r) => s + r.rmd, 0);

  if (ageYE < rmdAge) {
    return { notRequired: true, yearsUntil: rmdAge - ageYE, rmdAge, ageYE, inherited, inheritedTotal };
  }
  const divisor = getDistributionPeriod(ageYE);
  const regular = ownedIRAs(accounts);
  const annuities = ownedAnnuities(accounts);
  const regularTotal = regular.reduce((s, a) => s + (a.balance || 0), 0);
  const annuityFMVTotal = annuities.reduce((s, a) => s + (a.fair_market_value || a.balance || 0), 0);
  const annuityIncome = annuities.reduce((s, a) => s + (a.annual_income || 0), 0);
  const totalFMV = regularTotal + annuityFMVTotal;
  const totalRMD = divisor > 0 ? totalFMV / divisor : 0;
  const additionalRMD = Math.max(0, totalRMD - annuityIncome);
  return {
    ageYE, divisor, regularTotal, annuityFMVTotal, annuityIncome,
    totalFMV, totalRMD, additionalRMD,
    rmdSatisfied: annuityIncome >= totalRMD, rmdAge,
    inherited, inheritedTotal,
  };
}

// 10-year projection of the OWNED aggregate (existing behavior), plus an
// independent schedule for each inherited account.
export function calculateProjections(client, growthRate, arbitrageRate = 0) {
  const currentYear = new Date().getFullYear();
  const dob = new Date(client.dob);
  const ageYE = currentYear - dob.getFullYear();
  const accounts = client.accounts || [];
  const annuityAccounts = ownedAnnuities(accounts);
  let nonAnnuityValue = ownedIRAs(accounts).reduce((s, a) => s + (a.balance || 0), 0);
  const projections = [], arbProjections = [];

  for (let i = 0; i < 10; i++) {
    const year = currentYear + i + 1;
    const projAge = ageYE + i + 1;
    const divisor = getDistributionPeriod(projAge);

    const annuityFMV = annuityAccounts.reduce((s, a) => {
      const fmv = a.fair_market_value || a.balance || 0;
      if (!a.annuity_end_date) return s + fmv;
      const endDate = new Date(a.annuity_end_date);
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      if (year > endYear) return s;
      const totalMonths = (endYear - currentYear) * 12 + endMonth;
      if (totalMonths <= 0) return s;
      const monthlyAmort = fmv / totalMonths;
      const monthsElapsed = Math.min((year - currentYear) * 12, totalMonths);
      return s + Math.max(0, fmv - monthlyAmort * monthsElapsed);
    }, 0);

    const fmv = nonAnnuityValue + annuityFMV;
    const totalRMD = divisor > 0 ? fmv / divisor : 0;
    const annuityIncome = annuityAccounts.reduce((s, a) => {
      if (!a.annuity_end_date) return s + (a.annual_income || 0);
      const endDate = new Date(a.annuity_end_date);
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      if (year < endYear) return s + (a.annual_income || 0);
      if (year === endYear) return s + (a.annual_income || 0) * (endMonth / 12);
      return s;
    }, 0);
    const additionalRMD = Math.max(0, totalRMD - annuityIncome);
    nonAnnuityValue = (nonAnnuityValue - additionalRMD) * (1 + growthRate);

    const row = { Year: year, AgeYE: projAge, NonAnnuityValue: Math.round(nonAnnuityValue), AnnuityFMV: Math.round(annuityFMV), Divisor: divisor, TotalRMD: Math.round(totalRMD), AnnuityIncome: Math.round(annuityIncome), AdditionalRMD: Math.round(additionalRMD) };
    projections.push(row);
    arbProjections.push({ ...row });   // preserved as in the original engine (arbitrage rate reported, not applied here)
  }

  const inherited = inheritedAccounts(accounts).map(a => ({
    id: a.id, account_number: a.account_number,
    ...projectInherited(a, client.dob, currentYear, growthRate, 10),
  }));

  return { projections, arbProjections, inherited };
}
